import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import ApplicationFilters from '../components/applications/ApplicationFilters';
import {
  MagnifyingGlassIcon,
  Bars3Icon,
  Squares2X2Icon,
  FunnelIcon,
  EllipsisVerticalIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ScrollableDropdown from '../components/ScrollableDropdown';
import FloatingMenu from '../components/FloatingMenu';

const TesterApplications = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // const { user } = useAuth(); // Removed unused user variable

  // Data State
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI State
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [showFilters, setShowFilters] = useState(false);

  // Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    developerId: 'all',
    version: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);


  // Effects
  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    const handleBugUpdated = () => {
      fetchApplications();
    };

    const handleStorage = (e) => {
      if (e.key === 'bugtracker:bug-updated') {
        handleBugUpdated();
      }
    };

    window.addEventListener('bugtracker:bug-updated', handleBugUpdated);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('bugtracker:bug-updated', handleBugUpdated);
      window.removeEventListener('storage', handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Handle auto-scroll from navigation
  useEffect(() => {
    if (location.state?.scrollToApp && applications.length > 0 && !loading) {
      const appElement = document.getElementById(`app-${location.state.scrollToApp}`);
      if (appElement) {
        setTimeout(() => {
          appElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          appElement.classList.add('highlight-app');
        }, 100);
      }
    }
  }, [location.state, applications, loading]);

  const fetchApplications = async () => {
    try {
      const { data } = await api.get('/api/applications/for-bug-reporting');
      // Enrich with builds
      const appsWithBuilds = await Promise.all(
        data.data.map(async (app) => {
          try {
            const { data: buildsData } = await api.get(`/api/applications/${app._id}/builds`);
            return { ...app, builds: buildsData.data || [] };
          } catch (error) {
            console.error(`Error fetching builds for app ${app._id}:`, error);
            return { ...app, builds: [] };
          }
        })
      );
      setApplications(appsWithBuilds);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to fetch applications');
      }
      setLoading(false);
    }
  };

  // Logic

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: 'all', developerId: 'all', version: '' });
    setSearchTerm('');
  };

  // Derived Data
  const uniqueDevelopers = useMemo(() => {
    const devs = new Map();
    applications.forEach(app => {
      if (app.developerId) {
        devs.set(app.developerId._id || app.developerId.name, {
          _id: app.developerId._id,
          name: app.developerId.name
        });
      }
    });
    return Array.from(devs.values());
  }, [applications]);

  const filteredApplications = useMemo(() => {
    const filtered = applications.filter(app => {
      // Search
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        app.name.toLowerCase().includes(term) ||
        (app.description || '').toLowerCase().includes(term) ||
        (app.developerId?.name || '').toLowerCase().includes(term);

      // Filters
      const matchesStatus = filters.status === 'all' || app.status === filters.status;
      const matchesDev = filters.developerId === 'all' ||
        (app.developerId?._id === filters.developerId) ||
        (app.developerId?.name === filters.developerId); // Handle both for robustness

      const matchesVersion = !filters.version || (app.version.includes(filters.version));

      return matchesSearch && matchesStatus && matchesDev && matchesVersion;
    });

    // Sort: Alphabetically
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [applications, searchTerm, filters]);

  const totalPages = Math.ceil(filteredApplications.length / pageSize);
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredApplications.slice(startIndex, startIndex + pageSize);
  }, [filteredApplications, currentPage, pageSize]);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // Actions


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 1. Top Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Title & Count */}
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Applications</h1>
              <span className="bg-indigo-100 text-indigo-700 py-0.5 px-2 rounded-full text-xs font-semibold">
                {filteredApplications.length}
              </span>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 w-full md:w-auto">
              {/* Search */}
              <div className="relative flex-1 md:w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search apps, devs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-1 focus:ring-indigo-500 text-sm transition-colors"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-2.5 flex items-center"
                    title="Clear search"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" />
                  </button>
                )}
              </div>

              {/* Primary Filters: Status */}
              <div className="hidden lg:block min-w-[140px]">
                <ScrollableDropdown
                  value={filters.status}
                  onChange={(value) => handleFilterChange('status', value)}
                  options={[
                    { value: 'all', label: 'All Statuses' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' },
                  ]}
                  placeholder="All Statuses"
                  buttonHeightClassName="h-10"
                  maxMenuHeightPx={240}
                  maxMenuHeightClassName="max-h-60"
                  buttonClassName="bg-gray-50 text-sm"
                />
              </div>

              {/* Primary Filters: Developer */}
              <div className="hidden lg:block min-w-[160px]">
                <ScrollableDropdown
                  value={filters.developerId}
                  onChange={(value) => handleFilterChange('developerId', value)}
                  options={[
                    { value: 'all', label: 'All Developers' },
                    ...uniqueDevelopers.map((dev) => ({ value: dev._id || dev.name, label: dev.name }))
                  ]}
                  placeholder="All Developers"
                  buttonHeightClassName="h-10"
                  maxMenuHeightPx={240}
                  maxMenuHeightClassName="max-h-60"
                  buttonClassName="bg-gray-50 text-sm"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-bold transition-all ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-gray-300 text-gray-700'} hover:bg-gray-50`}
                title="More Filters"
              >
                <FunnelIcon className="h-4 w-4" />
                <span className="hidden sm:inline">More Filters</span>
              </button>

              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  title="List View"
                >
                  <Bars3Icon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Grid View"
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="border-t border-gray-200 bg-gray-50/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <ApplicationFilters
                filters={filters}
                onChange={handleFilterChange}
                onClear={clearFilters}
                developers={uniqueDevelopers}
              />
            </div>
          </div>
        )}
      </div>

      {/* 2. Content */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredApplications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-gray-200 rounded-xl bg-white/50">
            <div className="bg-gray-50 rounded-full p-4 mb-4">
              <ExclamationCircleIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No applications found</h3>
            <p className="text-gray-500 mt-1 max-w-sm mx-auto">Try adjusting your search terms or filters to find what you're looking for.</p>
            <button onClick={clearFilters} className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium text-sm">Clear all filters</button>
          </div>
        ) : (
          <>
            {viewMode === 'list' ? (
              <div className="space-y-3">
                {paginatedApplications.map((app) => (
                  <div
                    key={app._id}
                    className={`bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 hover:shadow-md transition-shadow group w-full ${app.isArchived ? 'opacity-70 bg-gray-50' : ''}`}
                  >
                    {/* Left Section: Info */}
                    <div className="flex items-start lg:items-center gap-3 min-w-0 flex-1">
                      {/* App Icon */}
                      <div
                        className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold text-base shrink-0 shadow-sm"
                        style={{ backgroundColor: app.themeColor || '#4f46e5' }}
                      >
                        {app.name.charAt(0).toUpperCase()}
                      </div>

                      {/* Text Content */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-base font-bold text-gray-900 leading-tight group-hover:text-indigo-600 transition-colors truncate">
                            {app.name}
                          </h3>
                          {app.isArchived && (
                            <span className="bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider">
                              Archived
                            </span>
                          )}
                        </div>
                        
                        <a
                          href={app.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-gray-400 hover:text-indigo-600 hover:underline truncate block max-w-md mb-1.5"
                        >
                          {app.url}
                        </a>

                        {/* Metadata Row */}
                        <div className="flex items-center gap-2 text-[11px] text-gray-500 font-medium whitespace-nowrap overflow-hidden">
                          <span className="uppercase tracking-wide text-gray-400 text-[10px]">VERSION</span>
                          <span className="font-mono text-gray-600">{app.version}</span>
                          
                          <span className="text-gray-300 mx-1">•</span>
                          
                          <div className="flex items-center gap-1.5">
                            <span className={`h-1.5 w-1.5 rounded-full ${app.isArchived ? 'bg-gray-400' : (app.status === 'Active' ? 'bg-green-500' : app.status === 'Inactive' ? 'bg-gray-400' : 'bg-blue-500')}`} />
                            <span className={(!app.isArchived && app.status === 'Active') ? 'text-gray-700' : ''}>{app.isArchived ? 'Archived' : app.status}</span>
                          </div>

                          <span className="text-gray-300 mx-1">•</span>

                          <span className="uppercase tracking-wide text-gray-400 text-[10px]">DEV:</span>
                          <span className="truncate max-w-[120px]">{app.developerId?.name || 'Unknown'}</span>

                          <span className="text-gray-300 mx-1">•</span>

                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1" title="Open Bugs">
                              <span className="text-red-500 text-[10px]">🔴</span>
                              <span className="text-gray-700">{app.bugStats?.open || 0}</span>
                            </div>
                            <div className="flex items-center gap-1" title="Closed Bugs">
                              <span className="text-emerald-500 text-[10px]">🟢</span>
                              <span className="text-gray-700">{app.bugStats?.closed || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Section: Actions */}
                    <div className="flex items-center gap-2 self-end lg:self-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/applications/${app._id}/timeline`, { state: { from: 'tester-applications' } })}
                        className="h-8 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition-colors shadow-sm whitespace-nowrap flex items-center justify-center"
                      >
                        Timeline
                      </button>

                      <FloatingMenu
                        button={
                          <button className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 border border-gray-200 transition-colors" title="More options">
                            <EllipsisVerticalIcon className="h-4 w-4" />
                          </button>
                        }
                      >
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => navigate(`/applications/${app._id}`, { state: { from: 'tester-applications' } })}
                                className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                              >
                                View Details
                              </button>
                            )}
                          </Menu.Item>

                          {!app.isArchived && (
                            <Menu.Item>
                              {({ active }) => (
                                <Link
                                  to={`/bugs/new?application=${app._id}`}
                                  state={{ selectedApplication: app._id }}
                                  className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                                >
                                  Report Bug
                                </Link>
                              )}
                            </Menu.Item>
                          )}
                        </div>
                      </FloatingMenu>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* GRID VIEW (Refactored to be cleaner) */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedApplications.map((app) => (
                  <div key={app._id} id={`app-${app._id}`} className={`bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 hover:border-indigo-100 ${app.isArchived ? 'opacity-70 grayscale-[0.3]' : ''}`}>
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-6">
                        <div className="flex gap-4 items-start">
                          <div
                            className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-sm"
                            style={{ backgroundColor: app.themeColor || '#4f46e5' }}
                          >
                            {app.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-gray-900 leading-tight">
                                {app.name}
                              </h3>
                              {app.isArchived && (
                                <span className="bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider border border-gray-200">
                                  Archived
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-block h-2 w-2 rounded-full ${app.isArchived ? 'bg-gray-400' : (app.status === 'Active' ? 'bg-green-500' : app.status === 'Inactive' ? 'bg-gray-300' : 'bg-blue-500')}`} />
                              <span className="text-xs text-gray-500 font-medium">{app.isArchived ? 'Archived' : app.status}</span>
                              <span className="text-xs text-gray-300">•</span>
                              <span className="text-xs text-gray-500">{app.developerId?.name || 'Unknown'}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="space-y-4 mb-6">
                        <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem] leading-relaxed">
                          {app.description || 'No description available for this application.'}
                        </p>

                        {/* Credentials Tag */}
                        <div className="flex flex-wrap gap-2">
                          {(app.users && app.users.length > 0) ? (
                            <span className="inline-flex items-center text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md font-medium">
                              <UserIconPlaceholder className="h-3 w-3 mr-1.5" />
                              Credentials Ready
                            </span>
                          ) : null}
                        </div>

                        <div className="flex items-center gap-4 pt-4 border-t border-gray-50">
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Version</span>
                            <span className="text-sm font-mono text-gray-700">{app.version}</span>
                          </div>
                          <div className="flex flex-col flex-1">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">URL</span>
                            <a href={app.url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline truncate" onClick={e => e.stopPropagation()}>
                              {app.url.replace(/^https?:\/\//, '')}
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Actions Footer */}
                      <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                        {app.isArchived ? (
                          <span className="text-xs font-bold text-gray-500 bg-gray-100 px-3 py-2 rounded-md border border-gray-200 italic">
                            Archived
                          </span>
                        ) : (
                          <Link
                            to={`/bugs/new?application=${app._id}`}
                            state={{ selectedApplication: app._id }}
                            className="text-sm font-medium text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-md transition-colors shadow-sm"
                          >
                            Report Bug
                          </Link>
                        )}
                        <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors">
                            <EllipsisVerticalIcon className="h-5 w-5" />
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 top-full mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                              <div className="py-1">
                                <Menu.Item>
                                  {({ active }) => (
                                    <button onClick={() => navigate(`/applications/${app._id}`, { state: { from: 'tester-applications' } })} className={`${active ? 'bg-gray-100' : ''} block w-full px-4 py-2 text-left text-sm text-gray-700`}>View Details</button>
                                  )}
                                </Menu.Item>
                                <Menu.Item>
                                  {({ active }) => (
                                    <Link to={`/applications/${app._id}/timeline`} className={`${active ? 'bg-gray-100' : ''} block w-full px-4 py-2 text-left text-sm text-gray-700`}>Timeline</Link>
                                  )}
                                </Menu.Item>
                              </div>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {filteredApplications.length > 0 && (
              <div className="mt-8 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 bg-white rounded-lg shadow-sm">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-bold">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-bold">{Math.min(currentPage * pageSize, filteredApplications.length)}</span> of{' '}
                      <span className="font-bold">{filteredApplications.length}</span> apps
                    </p>
                    <div className="w-36">
                      <ScrollableDropdown
                        value={String(pageSize)}
                        onChange={(value) => {
                          setPageSize(Number(value));
                          setCurrentPage(1);
                        }}
                        options={[
                          { value: '10', label: '10 per page' },
                          { value: '20', label: '20 per page' },
                          { value: '50', label: '50 per page' },
                        ]}
                        placeholder={`${pageSize} per page`}
                        buttonHeightClassName="h-8"
                        maxMenuHeightPx={200}
                        maxMenuHeightClassName="max-h-48"
                        buttonClassName="px-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ⏮
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ◀
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) pageNum = i + 1;
                        else if (currentPage <= 3) pageNum = i + 1;
                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                        else pageNum = currentPage - 2 + i;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                              ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-700'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ▶
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ⏭
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Drawer removed */}
      </div>
    </div>
  );
};

// Helper for icon since we didn't import UserIcon
const UserIconPlaceholder = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

export default TesterApplications;
